import * as yup from 'yup';
import { useExperiment, PROMO_CODE_CONTENT_KEY } from '@web/experiments';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
} from '../../cms';
import { schema as calloutSchema } from '../../cms/builders/quiz_callout';
import { addonSectionSchema } from './addon_sections';
import { addonModalSchema } from './addon_modal';

export const quizNextButtonSchema = yup
  .object({
    desktopCTA: yup.string(),
    mobileCTA: yup.string(),
  })
  .transform(value => {
    let { value: experimentValue } = useExperiment(value.test_name ?? null);
    experimentValue = experimentValue || 'control';
    let ctaVariants = value;
    if (ctaVariants?.component === 'flagship_ab_test') {
      const experimentVariation = ctaVariants.variants.find(
        variant => variant.variant_name === experimentValue,
      );
      ctaVariants = experimentVariation?.blocks[0] ?? [];
    }
    return {
      desktopCTA: ctaVariants.desktopCTA || 'Continue',
      mobileCTA: ctaVariants.mobileCTA || 'Continue',
    };
  });

const usePromoCodeVariant = variants => {
  const { value: promoCode } = useExperiment(PROMO_CODE_CONTENT_KEY, '');

  return (
    variants.find(variant => variant.promoCode === promoCode) ??
    variants.find(variant => variant.promoCode === 'default')
  );
};

const useExperimentValues = value => {
  let { value: experimentValue } = useExperiment(
    value.question[0].test_name ?? null,
  );
  experimentValue = experimentValue || 'control';
  let questionVariants = value.question[0];
  const experimentVariation = questionVariants?.variants?.find(
    variant => variant.variant_name === experimentValue,
  );

  questionVariants = experimentVariation?.blocks[0] ?? value.question[0];
  return questionVariants;
};

export const onboardingIntroSchema = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    headline: yup.string(),
    ctaButtonText: yup.string(),
    body: yup.string(),
    ollieLogo: requiredImageFromCMSSchema,
    fontColor: yup.string(),
    popUpText: yup.string().nullable(),
    popUpBgColor: yup.string(),
    popUpFontColor: yup.string(),
    popUpAlignment: yup.string(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      headline: question.headline,
      ctaButtonText: value.ctaButtonText || 'Continue',
      ollieLogo: value.ollieLogo,
      body: question.body,
      fontColor: question.fontColor || undefined,
      popUpText:
        usePromoCodeVariant(question.popUpTextVariants ?? [])?.message ||
        undefined,
      popUpBgColor: question.popUpBgColor || undefined,
      popUpFontColor: question.popUpFontColor || undefined,
      popUpAlignment: question.popUpAlignment || undefined,
    };
  });

export const pupName = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    subheadline: yup.string(),
    ctaButtonText: yup.string(),
    pupNameQuestionCaption: yup.string(),
    pupNameQuestionPrompt: yup.string(),
    pupNameCallout: calloutSchema.nullable(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      ctaButtonText: value.ctaButtonText || 'Continue',
      subheadline: question.subheadline,
      pupNameQuestionCaption: question.caption,
      pupNameQuestionPrompt: question.prompt,
      labelName: question.labelName || "Your pup's name",
      pupNameCallout: question.callout?.[0] ?? null,
    };
  });

export const pupBreed = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    subheadline: yup.string(),
    ctaButtonText: yup.string(),
    pupBreedQuestionCaption: yup.string(),
    pupBreedQuestionPrompt: yup.string(),
    pupBreedCallout: calloutSchema.nullable(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      labelName: question.labelName || "Your pup's breed",
      subheadline: question.subheadline,
      pupBreedQuestionCaption: question.caption,
      pupBreedQuestionPrompt: question.prompt,
      pupBreedCallout: question.callout?.[0] ?? null,
      ctaButtonText: value.ctaButtonText || 'Continue',
    };
  });

export const pupGender = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    ctaButtonText: yup.string(),
    pupGenderQuestionSubheadline: yup.string(),
    pupGenderQuestionPrompt: yup.string(),
    pupGenderCallout: calloutSchema.nullable(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      pupGenderQuestionSubheadline: question.subheadline,
      pupGenderQuestionPrompt: question.prompt,
      labelName: question.labelName || "Your pup's gender",
      pupGenderCallout: question.callout?.[0] ?? null,
    };
  });

export const pupStatus = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    labelName: yup.string(),
    ctaButtonText: yup.string(),
    pupStatusQuestionSubheadline: yup.string(),
    pupStatusQuestionPrompt: yup.string(),
    unspayedChipLabel: yup.string().required(),
    spayedChipLabel: yup.string().required(),
    pupStatusCallout: calloutSchema.nullable(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      pupStatusQuestionSubheadline: question.subheadline,
      pupStatusQuestionPrompt: question.prompt,
      labelName: question.labelName || "Your pup's spay/neuter status",
      spayedChipLabel: '{{pupStatusTermUppercase}}',
      unspayedChipLabel: 'Not {{pupStatusTermUppercase}}',
      pupStatusCallout: question.callout?.[0] ?? null,
    };
  });

export const weight = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    weightLabel: yup.string(),
    idealWeightLabel: yup.string(),
    ctaButtonText: yup.string(),
    weightQuestionSubheadline: yup.string(),
    weightQuestionPrompt: yup.string(),
    puppyIdealWeightLabel: yup.string(),
    idealWeightHelp: yup.string(),
    weightCallout: yup.array(calloutSchema),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      ctaButtonText: value.ctaButtonText || 'Continue',
      weightQuestionSubheadline: question.subheadline,
      weightQuestionPrompt: question.prompt,
      weightLabel: question.weightLabel || 'Pup’s current weight in lbs',
      idealWeightLabel:
        question.idealWeightLabel || 'Pup’s ideal weight in lbs (optional)',
      puppyIdealWeightLabel:
        question.puppyIdealWeightLabel ||
        'Pup’s ideal weight in lbs (optional)',
      idealWeightHelp:
        "If added, we'll use this weight to determine your pup's daily calories.",
      weightCallout: question.callout,
    };
  });

export const activity = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    activityQuestionPrompt: yup.string(),
    activityQuestionCallout: calloutSchema.nullable(),
    subheadline: yup.string(),
    levels: yup
      .array(
        yup.object({
          name: yup.string().required(),
          value: yup.string().required(),
          description: yup.string().required(),
          image: requiredImageFromCMSSchema,
        }),
      )
      .required(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      subheadline: question.subheadline,
      activityQuestionPrompt: question.prompt,
      activityQuestionCallout: question.callout?.[0] ?? null,
      levels: [
        {
          name: question.low_activity_card_title,
          value: 'Low',
          image: question.low_activity_image,
          description: question.low_activity_card_description,
        },
        {
          name: question.medium_activity_card_title,
          value: 'Moderate',
          image: question.medium_activity_image,
          description: question.medium_activity_card_description,
        },
        {
          name: question.high_activity_card_title,
          value: 'High',
          image: question.high_activity_image,
          description: question.high_activity_card_description,
        },
      ],
    };
  });

export const allergies = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    allergyQuestionPrompt: yup.string(),
    allergyQuestionSubheadline: yup.string(),
    labelName: yup.string(),
    allergiesSelectionPrompt: yup.string(),
    allergiesSelectionCaption: yup.string(),
    allergiesSearchLabel: yup.string(),
    allergiesCallout: calloutSchema.nullable(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      allergyQuestionSubheadline: question.subheadline,
      allergyQuestionPrompt: question.prompt,
      labelName: question.labelName || 'Allergy',
      allergiesSelectionPrompt: question.selection_prompt,
      allergiesSelectionCaption: question.selection_caption,
      allergiesSearchLabel: question.selection_label_name || 'Other',
      allergiesSelectionSubheadline: question.selection_subheadline,
      allergiesCallout: question.callout?.[0] ?? null,
    };
  });

const giftVariantProductCardSchema = yup
  .object({
    name: yup.string().required(),
    value: yup.string().required(),
    image: nullableImageFromCMSSchema,
    minWeight: yup
      .number()
      .nullable()
      .transform(v => (isNaN(v) ? null : v)),
    maxWeight: yup
      .number()
      .nullable()
      .transform(v => (isNaN(v) ? null : v)),
    level: yup.string(),
    description: yup.string(),
    hidden: yup.boolean().default(false),
    pillText: yup.string(),
    pillVariant: yup.string(),
  })
  .transform(value => ({
    name: value.display_name,
    value: value.value,
    image: value.image,
    minWeight: value.minWeight,
    maxWeight: value.maxWeight,
    level: value.level || undefined,
    description: value.description,
    hidden: value.hidden,
    pillText: value.pillText,
    pillVariant: value.pillVariant || undefined,
  }));

const giftTypeSchema = yup
  .object({
    name: yup.string().required(),
    choices: yup.array(giftVariantProductCardSchema).required(),
    defaultChoice: yup.array(giftVariantProductCardSchema).required(),
  })
  .transform(value => ({
    name: value.name,
    choices: value.choices?.filter(item => !item.hidden) || undefined,
    defaultChoice:
      value.defaultChoice?.filter(item => !item.hidden) || undefined,
  }));

export const giftWithPurchase = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    imagePillText: yup.string(),
    imagePillVariant: yup.string(),
    headline: yup.string(),
    subheadline: yup.string(),
    giftTypes: yup.array(giftTypeSchema),
    body: yup.array(yup.mixed()),
  })
  .transform(({ body, ...value }) => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      imagePillText: question.imagePill,
      imagePillVariant: question.imagePillVariant || undefined,
      headline: question.headline,
      subheadline: question.subheadline,
      giftTypes: question.giftTypes || undefined,
      body: question.body,
    };
  });

export const extras = yup
  .object({
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    desktopCtaText: yup.string(),
    headline: yup.string(),
    subheadline: yup.string(),
    addonSections: yup.array(addonSectionSchema).required(),
    body: yup.array(yup.mixed()),
    modals: yup.array(addonModalSchema),
    pillText: yup.string(),
    pillVariant: yup.string(),
  })
  .transform(({ body, ...value }) => {
    const question = useExperimentValues(value);
    return {
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      desktopCtaText: value.ctaButtonText || 'Continue',
      headline: question.headline,
      subheadline: question.subheadline,
      addonSections: question.addon_sections,
      body: question.body,
      modals: question.modals,
      pillText: question.pillText,
      pillVariant: question.pillVariant,
    };
  });

export const userInfoSchema = yup
  .object({
    headline: yup.string(),
    subheadline: yup.string(),
    eyebrowText: yup.string(),
    emailLabel: yup.string().required(),
    firstNameLabel: yup.string().required(),
    ctaButtonText: yup.string(),
    image: requiredImageFromCMSSchema,
    eyebrowVariant: yup.string(),
  })
  .transform(value => {
    const question = useExperimentValues(value);
    return {
      headline: question.headline,
      subheadline: question.subheadline,
      eyebrowText: value?.question[0]?.eyebrow_text || value?.eyebrow_text,
      emailLabel: question.email_label || 'Email Address',
      firstNameLabel: question.first_name_label || 'Your Name',
      ctaButtonText: question.continue || 'Continue',
      image: question.image,
      eyebrowVariant: question.eyebrowVariant || undefined,
    };
  });

const abandonerMenuContentSchema = yup
  .object({
    headline: yup.string().required(),
    subheadline: yup.string().required(),
    multipetCallout: yup.string(),
    primaryCtaText: yup.string().required(),
    secondaryCtaText: yup.string(),
  })
  .transform(value => ({
    headline: value.headline,
    subheadline: value.subheadline,
    multipetCallout: value.multipetCallout,
    primaryCtaText: value.primaryCtaText,
    secondaryCtaText: value.secondaryCtaText,
  }));

export const quizAbandonerMenuSchema = yup
  .object({
    ollieLogo: requiredImageFromCMSSchema,
    mobileImage: requiredImageFromCMSSchema,
    desktopImage: requiredImageFromCMSSchema,
    imagePillText: yup.string(),
    imagePillVariant: yup.string(),
    quizContent: yup.array(abandonerMenuContentSchema).required(),
    planContent: yup.array(abandonerMenuContentSchema).required(),
    checkoutContent: yup.array(abandonerMenuContentSchema).required(),
  })
  .transform(({ body, ...value }) => {
    const question = useExperimentValues(value);
    return {
      ollieLogo: value.ollieLogo,
      mobileImage: question.mobileImage,
      desktopImage: question.desktopImage,
      imagePillText: question.pillText,
      imagePillVariant: question.pillVariant || undefined,
      quizContent: question.quizContent,
      planContent: question.planContent,
      checkoutContent: question.checkoutContent,
    };
  });
